/**
 * §4's other half — the settings on this widget that are the client's to change.
 *
 * `.plans/ai-product-placer/README.md` §3.3, v1.1: an owner or an admin edits "the
 * keys the type's schema marks `x-editor: client`", and nothing else. `WidgetConfigPanel`
 * is the table of facts and this is the form under it; `usage.setConfigByClient` is
 * the same rule on the server, which is what actually enforces it.
 *
 * ## Four decisions, and the fourth is the only one that shows
 *
 *   1. **The fields are the schema's, drawn by the same function the studio's form
 *      uses.** `components/portal/schemaFields.ts` `fieldsOf` walks the narrowed
 *      snapshot, `fieldOf` decides each widget, and a property's own `title` is its
 *      label — which is why `Previews per day` reads the same here as it does on the
 *      studio's client page. Nothing in this file knows what any of them mean, and
 *      nothing in it reads `widget_type` (`WidgetPage.test.tsx`'s source scan).
 *   2. **The resolver is the schema itself.** `configSchemaToZod` over the narrowed
 *      snapshot — the very function `usage.setConfigByClient` validates with, from
 *      `packages/shared`. `ARCHITECTURE.md` §Frontend asks for "the same schema the
 *      API validates with", and `components/studio/actionSchema.ts`'s own header says
 *      why a *studio* action form cannot have it: there is no object to share. Here
 *      there is one, so `minimum: 1` and `minItems: 1` are refused under the box
 *      rather than by a round trip.
 *   3. **The body is the keys that changed.** The route is a `PATCH` whose body is
 *      the changes (`packages/shared/src/schemas/usage.ts` `WidgetConfigChangesSchema`),
 *      so a form showing four properties of nine cannot rewrite the five it does not
 *      display. A box that was blank and is still blank is a key left out. **A box
 *      somebody emptied is not that box**: it used to be left out too, which the
 *      merge on the server reads as "leave it alone" — so emptying a setting and
 *      pressing Save closed the form as if it had saved, with the old value still
 *      there. It is a change now, by the rule the studio's forms use
 *      (`components/portal/schemaForm.ts` `clearedOf`): sent as the empty list or the
 *      empty text the schema takes, validated like any other value, and where the
 *      schema has no way to say "nothing" — a number, a list that must hold one —
 *      refused under the box in so many words.
 *   4. **Lowering the daily limit asks first.** It is the one change that takes
 *      something away from a site that is serving right now, and the dialog says so.
 *      Raising it does not ask, and neither does anything else: a confirmation on a
 *      change that only adds is the chrome DESIGN.md P7 deletes.
 */

import { useMemo, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'

import {
  configSchemaToZod,
  type ConfigField,
  type JsonSchemaObject,
  type WidgetView,
} from '@fareast/shared'
import { z } from 'zod'

import { useSetWidgetConfig } from '../../../api/client/widgets.js'
import { fieldErrorsOf, messageOf, problemPaths } from '../../../api/problem.js'
import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { useZodForm } from '../../../components/portal/Form.js'
import { Stack } from '../../../components/portal/Layout.js'
import { ConfirmDialog } from '../../../components/portal/Overlay.js'
import {
  fieldsOf,
  narrowedTo,
  type FieldSpec,
  type SchemaNode,
} from '../../../components/portal/schemaFields.js'
import {
  cannotBeEmptied,
  formValuesOf,
  isBlank,
  wireValuesOf,
  type FormValues,
} from '../../../components/portal/schemaForm.js'
import { Notice } from '../../../components/portal/Status.js'
import { Prose } from '../../../components/portal/Text.js'
/*
 * **The one import across the portal/studio line, and it is deliberate.**
 *
 * `ActionField` is the single "one control per `FieldWidget`" renderer and the widget
 * vocabulary it renders is `components/portal/schemaFields.ts`'s, not the studio's —
 * it sits under `components/studio/` only because the studio's action form was the
 * first thing to need it. The alternative is a second renderer over the same
 * `FieldSpec`, which is the one thing P5's DRY watchlist forbids by name. The file
 * that already crossed this line is `pages/client/billing/useIndicativePrice.ts`,
 * for `useDebounced`.
 */
import { ActionField } from '../../../components/studio/ActionField.js'
import { useFormat } from '../../../format/useFormat.js'

/**
 * The property a lowering asks about, by key.
 *
 * A key rather than a kind of widget: `site_daily` is a property of a config schema,
 * and this file could not read `widget.widget_type` if it wanted to. A schema without
 * one simply never opens the dialog.
 */
const DAILY_KEY = 'site_daily'

/**
 * The action field the server addresses a widget's whole configuration by.
 *
 * There is no action on this page — the client's door is a REST `PATCH` — and the
 * refusals still name `config.<key>`, because `usage.setConfigByClient` and
 * `widgets_set_config` raise one kind of refusal between them and the studio's tool
 * takes `{ widget, config }`. `api/problem.ts` `fieldErrorsOf` takes this and strips
 * it, exactly as `ActionForm` does from its `schema` override's `under`.
 */
const CONFIG_FIELD = 'config'

/** What a client may change on this widget: the narrowed schema, and its fields. */
export type EditableConfig = {
  /** The snapshot with only the `x-editor: client` properties on it. */
  readonly node: SchemaNode
  /** One field per property, in the schema's own order. */
  readonly fields: readonly FieldSpec[]
}

/**
 * The client's half of a config schema, or null when there is none.
 *
 * `configFieldsOf` in `packages/shared` is the one reader of a config schema — the
 * same function `WidgetConfigPanel` draws its table from and `usage.setConfigByClient`
 * filters with — so "which keys are the client's" is answered once, on both sides of
 * the wire. This narrows the schema to them and asks `fieldsOf` to draw it.
 *
 * Null for a schema with no client keys **and** for one whose client keys hold a
 * shape `fieldOf` will not draw. The second cannot happen — a config schema is flat
 * scalars, enums and string arrays by construction (`widgetConfig.ts`
 * `assertConfigSchemaShape`) — and a panel with no Edit button is the right answer if
 * it ever does.
 */
export function editableConfigOf(
  schema: JsonSchemaObject,
  fields: readonly ConfigField[],
): EditableConfig | null {
  const keys = new Set(fields.filter((one) => one.editor === 'client').map((one) => one.key))
  if (keys.size === 0) return null

  const node = narrowedTo(schema, (name) => keys.has(name))
  if (node === null) return null

  const drawn = fieldsOf(node)
  return drawn === null || drawn.length === 0 ? null : { node, fields: drawn }
}

export type WidgetConfigFormProps = {
  readonly widget: WidgetView
  readonly editable: EditableConfig
  /** Called when the change is saved, or the reader gave up on it. */
  readonly onClose: () => void
}

export function WidgetConfigForm({ widget, editable, onClose }: WidgetConfigFormProps) {
  const format = useFormat()
  const save = useSetWidgetConfig()

  const stored = useMemo(() => widget.config ?? {}, [widget.config])
  const { node, fields: declared } = editable
  /*
   * What the boxes opened holding, taken once, beside the values the form itself
   * takes once (`useForm`'s `defaultValues`). "Which box was emptied" is a fact about
   * this form's own boxes; read off `widget.config` as it is now, a box that opened
   * blank over a value somebody saved since would look emptied.
   */
  const [opened] = useState(() => storedIn(declared, widget.config ?? {}))
  // A setting that cannot be taken away from here is not labelled as one that can.
  const fields = useMemo(
    () =>
      declared.map((field) =>
        cannotBeEmptied(field, opened) ? { ...field, optional: false } : field,
      ),
    [declared, opened],
  )

  /*
   * The whole of the client's half, current values included — which is what the
   * resolver validates and not what the form sends.
   *
   * The server merges the changes over the **stored** config and validates all of
   * it, so a rule that spans two properties (or a `required` one the reader left
   * blank) is answered here exactly as it is there. What is sent is still only what
   * moved.
   */
  const base = useMemo(() => storedIn(fields, stored), [fields, stored])
  /** The controls that exist, for a refusal to be narrowed to. */
  const drawn = useMemo(() => new Set(fields.map((one) => one.name)), [fields])

  const resolver = useMemo(
    () => resolverFor(node, fields, base, opened),
    [node, fields, base, opened],
  )
  const form = useZodForm<FormValues, FormValues>(resolver, {
    defaultValues: formValuesOf(fields, stored),
  })

  /** The changes waiting behind the confirmation, or null when none are. */
  const [pending, setPending] = useState<Record<string, unknown> | null>(null)
  /** True when every one of the API's own errors is showing under a field already. */
  const [covered, setCovered] = useState(false)

  const send = (changes: Record<string, unknown>) => {
    setCovered(false)
    save.mutate(
      { ref: widget.ref, changes },
      {
        onSuccess: () => {
          setPending(null)
          onClose()
        },
        onError: (error) => {
          /*
           * The API's own refusal, put back on the field it names.
           *
           * `api/problem.ts` `fieldErrorsOf` is the one reader of a refusal's paths
           * and `ActionForm` uses the same one: both shapes (`errors[]` and a
           * `DomainError`'s `meta.path`), the narrowing to controls that exist, and
           * the strip of the `config.` the server addresses a widget's whole
           * configuration by. `usage.setConfigByClient`'s two refusals — a key the
           * schema has never heard of, and one the schema marks as the studio's —
           * are both the second shape.
           */
          const onFields = fieldErrorsOf(error, drawn, CONFIG_FIELD)
          for (const one of onFields) {
            form.setError(one.path, { type: 'server', message: one.message })
          }
          // The footer says nothing a field has already said (`ActionForm`'s rule).
          setCovered(onFields.length > 0 && onFields.length === problemPaths(error).length)
        },
      },
    )
  }

  const submit: SubmitHandler<FormValues> = (values) => {
    const changes = changesIn(wireValuesOf(fields, values, opened), stored)
    if (Object.keys(changes).length === 0) {
      onClose()
      return
    }
    if (lowersDailyLimit(changes, stored)) {
      setPending(changes)
      return
    }
    send(changes)
  }

  const daily = pending === null ? null : dailyLimitIn(pending)

  return (
    <>
      {/*
        `handleSubmit` answers a promise and `onSubmit` wants nothing back; the
        promise is the resolver's and is settled by the time anything here could act
        on it. `void` is the eslint-approved way of saying so.
      */}
      <form
        noValidate
        onSubmit={(event) => {
          void form.handleSubmit(submit)(event)
        }}
      >
        <Stack>
          {fields.map((field) => (
            <ActionField key={field.name} field={field} control={form.control} />
          ))}

          {save.isError && !covered ? (
            <Notice need role="alert">
              {messageOf(save.error)}
            </Notice>
          ) : null}

          <ButtonRow>
            <Button variant="primary" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save these settings'}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Leave them as they are
            </Button>
          </ButtonRow>
        </Stack>
      </form>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null)
        }}
        title="Lower what this widget can serve today?"
        confirmLabel={daily === null ? 'Lower it' : `Lower it to ${format.number(daily)} a day`}
        cancelLabel="Leave it where it is"
        busy={save.isPending}
        onConfirm={() => {
          if (pending !== null) send(pending)
        }}
      >
        <Prose>
          {widget.site} is set up to serve {format.number(storedDailyLimit(stored))} a day. Lowering
          it to {daily === null ? 'fewer' : format.number(daily)} takes effect at this
          widget&rsquo;s next check, and anything past the new limit sees the fallback for the rest
          of today.
        </Prose>
      </ConfirmDialog>
    </>
  )
}

/* ------------------------------------------------------------- the resolver */

/**
 * The narrowed snapshot, as the thing `useZodForm` resolves with.
 *
 * The form holds **text** — `Input type="number"` reads and writes strings, a
 * `<select>`'s value is a string over a numeric enum — and the schema is about the
 * values on the wire, so the two are joined by `wireValuesOf`: the form's state is
 * converted exactly as it will be sent, then handed to the schema's own validator,
 * and every issue it raises is set back on the field its path names. That is what
 * makes "the same schema the API validates with" true rather than aspirational.
 *
 * `z.record(z.string(), z.unknown())` is the outer shape because react-hook-form's
 * state is one — the fields are known at runtime and not at compile time — and the
 * refinement is where all of the checking is.
 */
function resolverFor(
  schema: SchemaNode,
  fields: readonly FieldSpec[],
  base: Record<string, unknown>,
  opened: Record<string, unknown>,
): z.ZodType<FormValues, FormValues> {
  const validate = configSchemaToZod(schema)

  return z.record(z.string(), z.unknown()).superRefine((values, ctx) => {
    /*
     * A box that opened with a value, is blank now, and that the schema has no way to
     * take the value away from. Left alone it is simply not sent, the stored value
     * fills its place below and passes, and the form closes over a setting that did
     * not change — so it is refused here, in a sentence, under the box.
     */
    for (const field of fields) {
      if (cannotBeEmptied(field, opened) && isBlank(values[field.name])) {
        ctx.addIssue({ code: 'custom', path: [field.name], message: CANNOT_BE_EMPTIED })
      }
    }
    const parsed = validate.safeParse({ ...base, ...wireValuesOf(fields, values, opened) })
    if (parsed.success) return
    for (const issue of parsed.error.issues) {
      ctx.addIssue({ code: 'custom', path: [...issue.path], message: issue.message })
    }
  })
}

/** What an emptied setting is told when emptying it is not something this form can do. */
const CANNOT_BE_EMPTIED =
  'This setting cannot be left empty from here, so nothing was saved. Give it a value, or ' +
  'ask the studio to take it off.'

/* --------------------------------------------------------------- the changes */

/**
 * The keys whose value moved — the body of the `PATCH`.
 *
 * Compared as JSON rather than by identity, because two of the shapes are arrays and
 * `['rug'] === ['rug']` is false. Order counts, and that is right: `origins` is a
 * list the widget service reads in order.
 */
export function changesIn(
  values: Record<string, unknown>,
  stored: Record<string, unknown>,
): Record<string, unknown> {
  const changes: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(values)) {
    if (JSON.stringify(value) !== JSON.stringify(stored[key])) changes[key] = value
  }
  return changes
}

/** The stored config, narrowed to the properties this form draws. */
function storedIn(
  fields: readonly FieldSpec[],
  stored: Record<string, unknown>,
): Record<string, unknown> {
  const values: Record<string, unknown> = {}
  for (const field of fields) {
    const value = stored[field.name]
    if (value !== undefined) values[field.name] = value
  }
  return values
}

/** True when the change takes the daily limit **down**. Raising it asks nothing. */
export function lowersDailyLimit(
  changes: Record<string, unknown>,
  stored: Record<string, unknown>,
): boolean {
  const next = dailyLimitIn(changes)
  const now = dailyLimitIn(stored)
  return next !== null && now !== null && next < now
}

function dailyLimitIn(values: Record<string, unknown>): number | null {
  const value = values[DAILY_KEY]
  return typeof value === 'number' ? value : null
}

/** The stored limit, for the sentence. Zero would be a schema violation, not a state. */
function storedDailyLimit(stored: Record<string, unknown>): number {
  return dailyLimitIn(stored) ?? 0
}
